import React from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Image from "next/image";
import { classNames } from "../../helpers/common.helpers";
import { urlForImage } from '~/lib/sanity.image';
import useLocalStorage from "../UseLocalStorage"
import { Button, Checkbox, Spinner } from '../../lib/mt-native'
import { MagnifyingGlassPlusIcon } from "@heroicons/react/24/outline";
import { createPortal } from "react-dom";

import CaptureForm from "../CaptureForm";

export default function DownloadSelector({profiles}) {
  const [activePreview, setActivePreview] = React.useState(null); 
  const [fileType, setFileType] = React.useState("pdf");     
  const [downloadItems, setDownloadItems] = React.useState({ selected: []});
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = useLocalStorage("downloadedEmail", "")
  const [isProcessing, setIsProcessing] = React.useState(false);
  const previewRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxImage, setLightboxImage] = React.useState(null);

  const handleOpen = () => setOpen(!open);

  const constructSanityFileUrl = (fileRef, fileType) => {
    if (!fileRef?.asset?._ref) return null;
    
    return `https://cdn.sanity.io/files/${process.env.NEXT_PUBLIC_SANITY_PROJECT_ID}/${process.env.NEXT_PUBLIC_SANITY_DATASET}/${fileRef.asset._ref
      .replace('file-', '')
      .replace(`-${fileType}`, `.${fileType}`)}`;
  };

  const getFileInfo = (file, fileType) => {
    const type = fileType?.toLowerCase() || 'pdf';
    
    switch (type) {
      case 'pdf':
        return {
          url: constructSanityFileUrl(file.pdfFile, 'pdf'),
          fileName: `${file.sku}-${file.title}.pdf`
        };
      case 'dwg':
        return {
          url: constructSanityFileUrl(file.dwgFile, 'dwg'),
          fileName: `${file.sku}-${file.title}.dwg`
        };
      case 'rvt':
        return {
          url: constructSanityFileUrl(file.rvtFle, 'rvt'),
          fileName: `${file.sku}-${file.title}.rvt`
        };
      default:
        return {
          url: constructSanityFileUrl(file.pdfFile, 'pdf'),
          fileName: `${file.sku}-${file.title}.pdf`
        };
    }
  };

  const getFormattedDate = () => {
    const now = new Date();
    return now.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(/\//g, '-').replace(',', '');
  };

  const handleFormSubmitted = React.useCallback((event) => {
    setIsProcessing(true);
    if (event.detail?.email && downloadItems.selected.length !== 0) {
      setValue(event.detail.email);
      fnDownLoad(event.detail.email);
    }
  }, [downloadItems.selected]);

  React.useEffect(() => {
    window.addEventListener('formSubmitted', handleFormSubmitted);
    return () => {
      window.removeEventListener('formSubmitted', handleFormSubmitted);
    };
  }, [handleFormSubmitted]);

  const handleDownloadClick = async () => {
    const downloadItemsString = Object.values(downloadItems.selected).map(item => item.sku).join(', ');
    if (!value) {
      setOpen(true);
    } else {
      setIsProcessing(true);
      // Send to email API
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: 'repeat user',
          email: value,
          subject: 'New Download User',
          message: `A new download request was submitted by (${value})`,
          templateName: 'download_user_template',
          downloadItems: downloadItemsString
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send email notification');
      }
      fnDownLoad();
    }
  };

  const fnDownLoad = async (emailValue) => {
    const effectiveValue = emailValue || value;
    
    if (!effectiveValue) {
      setIsProcessing(false);
      return;
    }

    if (window.isDownloading) return;
    window.isDownloading = true;

    try {
      const zip = new JSZip();
      const folderName = `Profiles_${getFormattedDate()}`;
      const folder = zip.folder(folderName);
      
      if (Object.keys(downloadItems.selected).length === 0) {
        setIsProcessing(false);
        return;
      }
      
      const downloadPromises = Object.values(downloadItems.selected).map(async (file) => {
        const { url, fileName } = getFileInfo(file, fileType);

        if (!url) return;

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        folder.file(fileName, blob);
      });

      await Promise.all(downloadPromises.filter(Boolean));
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${folderName}.zip`);
    } catch (error) {
      console.error("Error downloading files:", error);
    } finally {
      window.isDownloading = false;
      setIsProcessing(false);
    }
  };

  const currentCount = () => {
    return Object.keys(downloadItems.selected).length + " files";
  }

  const addEntryClick = (file) => {
    let updatedData = {}
    let isNew = true
    for (let i in downloadItems.selected) {
        if (file._key !== i) {
            updatedData[i] = downloadItems.selected[i]
        } else {
            isNew = false
        }        
    }
    if(isNew) {
        updatedData[file._key] = file
    }
    downloadItems.selected = updatedData
    setDownloadItems(downloadItems);
    setActivePreview(file);
  }

  const getTotalFiles = () => {
    if (!profiles) return 0;
    return profiles.reduce((count, profile) => 
      count + (profile.profileFiles?.length || 0), 0
    );
  };

  const isAllSelected = () => {
    return Object.keys(downloadItems.selected).length === getTotalFiles();
  };

  const handleSelectAll = () => {
    if (!profiles) return;
    
    if (isAllSelected()) {
      setDownloadItems({ selected: {} });
    } else {
      let updatedData = {};
      profiles.forEach(profile => {
        profile.profileFiles?.forEach(file => {
          updatedData[file._key] = file;
        });
      });
      setDownloadItems({ selected: updatedData });
    }
  };

  const handleLightbox = (file, e) => {
    e.stopPropagation();
    setLightboxImage(file.previewImage);
    setLightboxOpen(true);
  };

  React.useEffect(() => {
    const handleScroll = () => {
      if (!previewRef.current || !containerRef.current) return;
      
      const container = containerRef.current.getBoundingClientRect();
      const preview = previewRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      
      // Calculate maximum scroll distance to keep preview visible
      const maxScroll = Math.max(0, container.height - preview.height);
      
      // Calculate how far we've scrolled relative to the container
      const relativeScroll = (window.scrollY - container.top) / (container.height);
      
      // Apply a dampening factor (0.5) to reduce scroll speed
      const newTop = Math.max(0, Math.min(maxScroll, relativeScroll * container.height * 0.5));
      
      previewRef.current.style.transform = `translateY(${newTop}px)`;
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [activePreview]);

  // Render the lightbox in a portal
  const renderLightbox = () => {
    if (!lightboxOpen || !lightboxImage) return null;
    
    return createPortal(
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[99999] flex items-center justify-center p-4"
        onClick={() => setLightboxOpen(false)}
      >
        <div className="relative max-w-7xl max-h-[90vh] w-full">
          <Image
            src={urlForImage(lightboxImage).url()}
            alt="Preview"
            width={0}
            height={0}
            sizes="100vw"
            style={{ 
              width: '100%',
              height: 'auto',
              maxHeight: '90vh',
              objectFit: 'contain'
            }}
            className="h-auto w-auto"
          />
          <button 
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 text-white text-xl font-bold bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center"
          >
            ×
          </button>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 mt-10">
        <div>
            <div className="mb-4">
                <Button 
                    onClick={handleSelectAll}
                    className="rounded-none download-bg border-black border-2 text-black font-bold uppercase"
                    size="lg"
                >
                    {isAllSelected() ? 'Deselect All Files' : 'Select All Files'}
                </Button>
            </div>
            {profiles && profiles.map((profile, index) => (
                <div key={index} className="mb-10">
                    <h4 className="text-lg mb-4">{profile.title}</h4>
                    <div>
                        <ul className="fileList">
                        {profile.profileFiles && profile.profileFiles.map((file, index) => (
                            <li 
                                key={index} 
                                className={classNames(    
                                    'border-black flex download-bg border border-b-0 cursor-pointer w-full',
                                    file === activePreview ? 'selected-file' : ''                                    
                                 )}  
                                onClick={() => {addEntryClick(file)}}>
                                <button 
                                    onClick={(e) => handleLightbox(file, e)}
                                    className="shrink-0 p-4 hover:bg-gray-100"
                                >
                                    <MagnifyingGlassPlusIcon className="h-6 w-6" />
                                </button> 
                                <div className="grid grid-cols-[80px_1fr_60px] md:grid-cols-[180px_1fr_60px] flex-1 items-center">                                
                                    <div className="px-6 overflow-hidden">
                                        {file.sku ? (
                                            <h4 className="text-sm md:text-lg">{file.sku}</h4>
                                        ) : (
                                            <h4 className="text-sm md:text-lg">&nbsp;</h4>
                                        )}
                                    </div>                            
                                    <div className="px-6 overflow-hidden">
                                        <h4 className="text-sm md:text-lg">{file.title}</h4>
                                    </div>
                                    <div className="px-4 flex justify-center">
                                        <Checkbox 
                                            checked={!!downloadItems.selected[file._key]}
                                            readOnly
                                            className="border-2 border-black rounded-none peer relative appearance-none w-5 h-5 cursor-pointer transition-all checked:bg-gray-900 checked:border-gray-900 before:content[''] before:block before:bg-white before:w-12 before:h-12 before:rounded-full before:absolute before:top-2/4 before:left-2/4 before:-translate-y-2/4 before:-translate-x-2/4 before:opacity-0 hover:before:opacity-10"                                                                   
                                        />
                                    </div>  
                                </div>
                            </li>
                        ))}
                        </ul>
                    </div>
                </div> 
            ))}
            <div>
                <div>
                    <div className="flex flex-col sm:flex-row mb-10">  
                        <div className="w-full sm:basis-2/3 items-center flex text-sm sm:text-base mb-4 sm:mb-0">
                            Please select the file type you prefer:
                        </div>  
                        <div className="w-full sm:basis-1/3"> 
                            <div className="flex justify-between gap-2">
                                <Button 
                                    onClick={() => {setFileType('pdf')}} 
                                    className={classNames(    
                                        'rounded-none download-bg border-black border-2 text-black font-bold',
                                        fileType === 'pdf' ? 'selected-bg' : ''                                    
                                     )} 
                                    size="lg"
                                >PDF</Button>
                                <Button 
                                    onClick={() => {setFileType('dwg')}} 
                                    className={classNames(    
                                        'rounded-none download-bg border-black border-2 text-black font-bold',
                                        fileType === 'dwg' ? 'selected-bg' : ''                                    
                                     )} 
                                    size="lg"
                                >DWG</Button>
                                <Button 
                                    onClick={() => {setFileType('rvt')}} 
                                    className={classNames(    
                                        'rounded-none download-bg border-black border-2 text-black font-bold',
                                        fileType === 'rvt' ? 'selected-bg' : ''                                    
                                     )} 
                                    size="lg"
                                >RVT</Button>
                            </div>                           
                        </div>                    
                    </div>
                </div>
                <div>
                    <div className="flex flex-row">
                        <div className="basis-2/3 items-center flex">
                            {isProcessing ? (
                                <div className="flex items-center gap-2">
                                    <Spinner className="h-6 w-6" />
                                    <span>Processing your download...</span>
                                </div>
                            ) : (
                                `There are ${currentCount()} ready to download.`
                            )}
                        </div> 
                        <div className="basis-1/3">
                            <Button 
                                onClick={handleDownloadClick}
                                disabled={isProcessing}
                                size="lg" 
                                className="rounded-none w-full align-middle select-none download-bg hover:selected-bg border-black border-2 text-black font-bold text-center uppercase transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-sm py-3.5 px-7 bg-gray-900 shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-gray-900/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none gap-3"
                            >
                                {isProcessing ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <Spinner className="h-4 w-4" />
                                        Processing...
                                    </div>
                                ) : (
                                    'Download'
                                )}
                            </Button>
                        </div> 
                    </div>
                </div>
            </div>            
        </div>
        <div className="mt-12 lg:mt-0 relative" ref={containerRef}>
            {activePreview && activePreview.previewImage && (
              <div ref={previewRef} className="mx-0 lg:mx-12 relative">
                <Image
                  src={urlForImage(activePreview.previewImage).url()}
                  alt={activePreview.title ? activePreview.title : 'preview'}
                  width={0}
                  height={0}
                  sizes="100vw"
                  style={{ 
                    height: 'auto', 
                    maxHeight: 'calc(100vh - 2rem)',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }} 
                  className="h-auto w-auto"
                />
              </div>
            )}
        </div> 
        <CaptureForm open={open} handleOpen={handleOpen} downloadItems={downloadItems} />        
        {renderLightbox()}
    </div>
  );
}